/* relix-attr.js v2.1 (2026-10-18)
 * Captures ad click IDs, UTMs, Google/Meta extras, referrer and landing page.
 * Last touch: overwritten on each new tagged or externally referred landing,
 *   all keys rewritten (missing ones blanked) so sources never mix.
 * First touch: written once, never overwritten.
 * Also stores the GA4 client id (from _ga) so GA4 behavior joins to orders.
 * The 9 v1 attribute names are unchanged, so the current orders loader keeps working.
 * v2.1: when a parameter repeats in the URL, the LAST non-empty value wins.
 *   Meta catalog ads append the ad-level URL parameters after the feed's own
 *   utm_source=facebook tags, so last-wins keeps {{site_source_name}} (ig/fb).
 */
(function () {
  'use strict';

  var LT_COOKIE = '_relix_attr'; // same name as v1 so existing visitors carry over
  var FT_COOKIE = '_relix_ft';
  var COOKIE_DAYS = 90;

  var URL_KEYS = [
    'gclid', 'gbraid', 'wbraid', 'fbclid',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gad_campaignid', 'gad_source', 'srsltid',
    'rlx_net', 'rlx_pl'
  ];
  var META_KEYS = ['rlx_ref', 'rlx_lp', 'rlx_ts'];
  var LT_KEYS = URL_KEYS.concat(META_KEYS);

  // Hosts that are not a marketing source (own store, checkout, payment hops)
  var INTERNAL_HOST = /(^|\.)(relix\.shop|myshopify\.com|shopify\.com|shop\.app|relixmarketplace\.com|paypal\.com|afterpay\.com|klarna\.com|affirm\.com)$/i;

  // consent
  function consentAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return true;
      return cp.analyticsProcessingAllowed() || cp.marketingAllowed();
    } catch (e) { return true; }
  }

  // cookies
  function readCookie(name) {
    try {
      var m = document.cookie.match('(?:^|;)\\s*' + name + '=([^;]*)');
      return m ? JSON.parse(decodeURIComponent(m[1])) : null;
    } catch (e) { return null; }
  }
  function writeCookie(name, data) {
    try {
      var exp = new Date(Date.now() + COOKIE_DAYS * 864e5).toUTCString();
      document.cookie = name + '=' + encodeURIComponent(JSON.stringify(data)) +
        '; path=/; expires=' + exp + '; SameSite=Lax; Secure';
    } catch (e) {}
  }
  function gaClientId() {
    try {
      var m = document.cookie.match(/(?:^|;)\s*_ga=([^;]*)/);
      if (!m) return '';
      var p = decodeURIComponent(m[1]).split('.');
      return p.length >= 4 ? p[p.length - 2] + '.' + p[p.length - 1] : '';
    } catch (e) { return ''; }
  }

  // touch detection
  function paramsFromURL() {
    var sp = new URLSearchParams(window.location.search);
    var found = {};
    URL_KEYS.forEach(function (k) {
      // last non-empty occurrence wins (ad-level params override feed params)
      var all = sp.getAll(k).filter(function (v) { return v; });
      if (all.length) found[k] = all[all.length - 1].slice(0, 255);
    });
    return found;
  }
  function referrerHost() {
    try { return document.referrer ? new URL(document.referrer).hostname.toLowerCase() : ''; }
    catch (e) { return ''; }
  }
  function isExternal(host) {
    return !!host && host !== window.location.hostname.toLowerCase() && !INTERNAL_HOST.test(host);
  }

  // Returns { touch, isNew }. A new touch = tagged URL or external referrer.
  // Direct visits (no params, no external referrer) keep the stored last touch.
  function resolveTouch() {
    var params = paramsFromURL();
    var ref = referrerHost();
    var ext = isExternal(ref);
    if (Object.keys(params).length || ext) {
      var t = params;
      t.rlx_ref = ext ? ref : '';
      t.rlx_lp = window.location.pathname.slice(0, 200);
      t.rlx_ts = new Date().toISOString();
      return { touch: t, isNew: true };
    }
    return { touch: readCookie(LT_COOKIE), isNew: false };
  }

  // cart attributes
  function buildAttrs() {
    var lt = readCookie(LT_COOKIE);
    var ft = readCookie(FT_COOKIE);
    var attrs = {};
    if (lt) {
      LT_KEYS.forEach(function (k) { attrs[k] = lt[k] ? String(lt[k]) : ''; });
    }
    if (ft) attrs.rlx_ft = JSON.stringify(ft).slice(0, 1000);
    var cid = gaClientId();
    if (cid) attrs.rlx_ga_cid = cid;
    return attrs;
  }
  function differs(cartAttrs, attrs) {
    cartAttrs = cartAttrs || {};
    return Object.keys(attrs).some(function (k) {
      return (cartAttrs[k] || '') !== (attrs[k] || '');
    });
  }
  function syncCart() {
    var attrs = buildAttrs();
    if (!Object.keys(attrs).length) return;
    _origFetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!differs(cart.attributes, attrs)) return;
        return _origFetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        });
      })
      .catch(function () {});
  }
  function pushAttrsNow() {
    var attrs = buildAttrs();
    if (!Object.keys(attrs).length) return;
    _origFetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () {});
  }

  // add-to-cart intercepts (fetch and XHR)
  var _origFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url.indexOf('/cart/add') !== -1) pushAttrsNow();
    } catch (e) {}
    return _origFetch.apply(this, arguments);
  };
  var _origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._relixUrl = url;
    return _origOpen.apply(this, arguments);
  };
  var _origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    try {
      if (this._relixUrl && String(this._relixUrl).indexOf('/cart/add') !== -1) pushAttrsNow();
    } catch (e) {}
    return _origSend.apply(this, arguments);
  };

  // main
  function init() {
    if (!consentAllowed()) {
      document.addEventListener('visitorConsentCollected', init, { once: true });
      return;
    }
    var r = resolveTouch();
    if (r.isNew) writeCookie(LT_COOKIE, r.touch);
    if (!readCookie(FT_COOKIE)) {
      writeCookie(FT_COOKIE, r.isNew ? r.touch : {
        rlx_ref: '', rlx_lp: window.location.pathname.slice(0, 200), rlx_ts: new Date().toISOString()
      });
    }
    syncCart();
    // _ga is set async by gtag; retry once so the client id lands on first pageview
    if (!gaClientId()) setTimeout(syncCart, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
